/**
 * WarRoomWalkthrough — full lineup-to-DK animated walkthrough.
 *
 * Modal component triggered by the "How It Works" button in War Room. Renders
 * a 6-step visual guide of the exact workflow experienced DFS players already
 * know: DK CSV export → drop into WarPaint → auto-optimize → download merged
 * CSV → upload to DK → confirm. Guaranteed-unique lineups baked in.
 *
 * Each step has:
 *   - Sequence number + progress dots
 *   - Icon + short instruction
 *   - Mock UI illustration built with CSS/SVG so we don't need recorded video
 *   - Animated highlight ring on the "thing to click"
 *
 * Auto-advances every 5s; pauses on hover. Users can click through manually
 * with Next / Prev buttons or the progress dots.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  X, CaretRight, CaretLeft, Play, Pause, SignIn, Target, FileArrowDown,
  UploadSimple, Lightning, DownloadSimple, PaperPlaneTilt, CheckCircle,
  ChartLineUp, Trophy, PuzzlePiece, Rocket, HardDrive,
} from "@phosphor-icons/react";

const STEPS = [
  {
    icon: SignIn,
    title: "Sign in to WarPaint",
    body: "Email + password, or the beta code if you have one. That's the last time you'll leave the WarPaint tab until your lineups are on DraftKings.",
    time: "~10 seconds",
    illustration: "login",
  },
  {
    icon: ChartLineUp,
    title: "Set projections + exposure locks",
    body: "On the Optimizer tab, tweak whichever players you feel strong about. Two locks / boosts / excludes / caps is all DK's Two-Touch rule requires — but the more you lean in, the more your portfolio reflects your actual read.",
    time: "~2 minutes",
    illustration: "optimizer",
  },
  {
    icon: Trophy,
    title: "Reserve your entries on DraftKings",
    body: "Open DraftKings in another tab. Enter the contests you want — Milly Maker, 20-Max Play Action, 3-Max, whatever. You do this exactly like you always have; WarPaint doesn't change this step.",
    time: "~2 minutes",
    illustration: "dk-lobby",
  },
  {
    icon: FileArrowDown,
    title: "Export your entries from DraftKings",
    body: "On any DK contest you've reserved, click Manage → Export Entries. DK gives you a CSV file with every reserved slot's Entry ID. Save it anywhere — WarPaint reads it in seconds.",
    time: "~15 seconds per contest",
    illustration: "dk-export",
  },
  {
    icon: UploadSimple,
    title: "Drop the CSV into WarPaint War Room",
    body: "In the Multi-Contest · Auto-Route panel at the top of War Room, drop the DK CSV. WarPaint parses every reserved slot, classifies each contest (Milly / 20-Max / 3-Max / Single-Entry / Cash), and shows the full breakdown.",
    time: "~5 seconds",
    illustration: "upload",
  },
  {
    icon: Target,
    title: "Set your lineup counts per contest",
    body: "By default WarPaint fills every reserved slot. Want to only fill 38 of your 150 Milly slots? Type 38 in the # Lineups column. Contests sharing a preset (e.g. two 150-Millys) get one shared unique-lineup pool split across both.",
    time: "~10 seconds",
    illustration: "counts",
  },
  {
    icon: Lightning,
    title: "Auto-Optimize All Contests",
    body: "One click. WarPaint applies the stack shapes, ownership window and diversity rules matched to each contest's field size, and generates guaranteed-unique lineups across the whole portfolio.",
    time: "~30 seconds for 340 lineups",
    illustration: "optimize",
  },
  {
    icon: DownloadSimple,
    title: "Download the merged CSV",
    body: "One CSV, all your contests. Every row has the correct Entry ID + Contest ID pre-filled — DK routes each lineup to its correct contest automatically on upload.",
    time: "~1 second",
    illustration: "download",
  },
  {
    icon: PaperPlaneTilt,
    title: "Upload to DraftKings + Confirm",
    body: "Drag the merged CSV onto DraftKings' bulk upload page. Click Import on DK — every lineup lands in its correct contest.",
    time: "~10 seconds",
    illustration: "push",
    hint: "This is the ONE click on DraftKings that Chrome's security model requires — nobody can automate this, WarPaint included.",
  },
];

const AUTO_ADVANCE_MS = 5500;

export default function WarRoomWalkthrough({ open, onClose }) {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [hovering, setHovering] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setIdx(0);
    setPlaying(true);
  }, [open]);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!open || !playing || hovering) return;
    timerRef.current = setTimeout(() => {
      setIdx((i) => (i + 1 < STEPS.length ? i + 1 : i));
    }, AUTO_ADVANCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [idx, playing, hovering, open]);

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => Math.min(STEPS.length - 1, i + 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const step = STEPS[idx];
  const Icon = step.icon;
  const isLast = idx === STEPS.length - 1;

  return (
    <div
      data-testid="walkthrough-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-6 backdrop-blur-md"
      style={{ background: "rgba(0,0,0,0.85)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className="relative w-full max-w-5xl bg-gradient-to-br from-[#0a1a10] via-[#0a0a0a] to-[#0a0a0a] border border-[#39FF14]/30 overflow-hidden"
        style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.6), 0 0 60px rgba(57,255,20,0.1)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-3 flex-wrap">
            <div className={`inline-flex items-center gap-2 px-2 py-1 border ${
              step.section === "boost" ? "border-[#FF8A00]/60" : "border-[#39FF14]/40"
            } bg-black/40`}>
              <span className={`mono text-[9px] uppercase tracking-[0.28em] font-bold ${
                step.section === "boost" ? "text-[#FF8A00]" : "text-[#39FF14]"
              }`}>
                {step.section === "boost" ? "One-Click DK CSV Upload" : "How It Works · Sign-in to DK"}
              </span>
            </div>
            <span className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
              Step {idx + 1} of {STEPS.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPlaying((p) => !p)}
              className="p-2 text-zinc-500 hover:text-white transition-colors"
              data-testid="walkthrough-playpause"
              title={playing ? "Pause auto-advance" : "Play"}
            >
              {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
            </button>
            <button
              onClick={onClose}
              data-testid="walkthrough-close"
              className="p-2 text-zinc-500 hover:text-[#FF3B30] transition-colors"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 md:grid-cols-2 min-h-[420px]">
          <div className="p-6 md:p-8 border-r border-zinc-800">
            <div className="w-12 h-12 bg-[#39FF14]/10 border border-[#39FF14] flex items-center justify-center mb-4">
              <Icon size={22} weight="fill" className="text-[#39FF14]" />
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] font-bold mb-1.5">
              {step.time}
            </div>
            <h2 className="heading text-white text-3xl font-black leading-tight mb-3">
              {step.title}
            </h2>
            <p className="text-sm text-zinc-300 leading-relaxed">
              {step.body}
            </p>
            {step.hint && (
              <div className="mt-4 flex items-start gap-2 text-[11px] text-zinc-400 leading-relaxed border-l-2 border-[#39FF14] pl-3">
                <CheckCircle size={12} weight="fill" className="text-[#39FF14] flex-shrink-0 mt-0.5" />
                <span>{step.hint}</span>
              </div>
            )}
          </div>

          <div className="p-6 md:p-8 bg-black/30 flex items-center justify-center min-h-[300px]">
            <StepIllustration kind={step.illustration} />
          </div>
        </div>

        {/* Progress dots + nav */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800">
          <button
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            data-testid="walkthrough-prev"
            className="inline-flex items-center gap-1 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed text-[10px] uppercase tracking-widest font-bold"
          >
            <CaretLeft size={12} weight="bold" /> Prev
          </button>

          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`walkthrough-dot-${i}`}
                className={`h-1.5 transition-all ${
                  i === idx ? "w-8 bg-[#39FF14]" : "w-1.5 bg-zinc-700 hover:bg-zinc-500"
                }`}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>

          <button
            onClick={() => (isLast ? onClose() : setIdx((i) => i + 1))}
            data-testid="walkthrough-next"
            className="inline-flex items-center gap-1 bg-[#39FF14] hover:bg-[#2ee30d] text-black font-black uppercase tracking-widest text-[10px] py-2 px-3 transition-colors"
          >
            {isLast ? "Got It" : "Next"}
            {!isLast && <CaretRight size={12} weight="bold" />}
          </button>
        </div>

        {/* Auto-advance progress bar */}
        {playing && !hovering && (
          <div
            key={`${idx}-${playing}-${hovering}`}
            className="absolute bottom-0 left-0 h-0.5 bg-[#39FF14]"
            style={{ animation: `wp-progress ${AUTO_ADVANCE_MS}ms linear forwards` }}
          />
        )}
      </div>

      <style>{`
        @keyframes wp-progress { from { width: 0% } to { width: 100% } }
        @keyframes wp-pulse-ring { 0%, 100% { box-shadow: 0 0 0 0 rgba(57,255,20,0.7) } 50% { box-shadow: 0 0 0 6px rgba(57,255,20,0) } }
        @keyframes wp-slide-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
      `}</style>
    </div>
  );
}

// -------------------------------------------------------------------
// Step-specific mock UI illustrations. Pure CSS/SVG so we don't need
// recorded footage; each one echoes the actual WarPaint / DK screen.
// -------------------------------------------------------------------
function StepIllustration({ kind }) {
  const shared = "w-full max-w-[300px] animate-[wp-slide-in_0.4s_ease-out]";
  switch (kind) {
    case "login":
      return (
        <div className={shared}>
          <MockCard title="WarPaint DFS · Sign In">
            <div className="text-[9px] text-zinc-500 mb-1">EMAIL</div>
            <div className="mono text-[11px] text-white bg-black border border-zinc-800 px-2 py-1.5 mb-2">
              you@email.com
            </div>
            <div className="text-[9px] text-zinc-500 mb-1">PASSWORD</div>
            <div className="mono text-[11px] text-zinc-400 bg-black border border-zinc-800 px-2 py-1.5 mb-3">
              ••••••••••
            </div>
            <PulseButton>SIGN IN</PulseButton>
          </MockCard>
        </div>
      );
    case "optimizer":
      return (
        <div className={shared}>
          <MockCard title="Optimizer · Player Overrides">
            <MockRow name="J. Jefferson" pos="WR · MIN" ownArrow="down" />
            <MockRow name="C. McCaffrey" pos="RB · SF" locked />
            <MockRow name="P. Mahomes" pos="QB · KC" boosted />
            <MockRow name="Rams DST" pos="DST · LAR" locked />
            <div className="mt-3 mono text-[9px] text-[#39FF14] uppercase tracking-widest text-center">
              4 touches · Two-Touch rule ✓
            </div>
          </MockCard>
        </div>
      );
    case "dk-lobby":
      return (
        <div className={shared}>
          <MockCard title="draftkings.com/lobby" host>
            <MockContest name="$6M Milly Maker" fee="$20" reserved={150} />
            <MockContest name="$100K Play Action" fee="$10" reserved={20} />
            <MockContest name="$25K 3-Max" fee="$50" reserved={3} />
            <div className="mt-3 mono text-[10px] text-white text-center border-t border-zinc-800 pt-2">
              173 entries · $3,350 buy-in
            </div>
          </MockCard>
        </div>
      );
    case "dk-export":
      return (
        <div className={shared}>
          <MockCard title="draftkings.com · Manage Entries" host>
            <div className="text-[10px] text-zinc-400 mb-2">$6M Milly Maker · 150/150</div>
            <PulseButton dk>EXPORT ENTRIES</PulseButton>
            <div className="mt-3 flex items-center gap-2 text-[10px] text-zinc-500">
              <FileArrowDown size={12} weight="fill" />
              <span className="mono">entries_MillyMaker_142856.csv</span>
            </div>
          </MockCard>
        </div>
      );
    case "upload":
      return (
        <div className={shared}>
          <MockCard title="War Room · Multi-Contest Auto-Route">
            <div className="border-2 border-dashed border-[#39FF14]/50 py-4 flex flex-col items-center gap-1"
                 style={{ animation: "wp-pulse-ring 1.8s ease-in-out infinite" }}>
              <UploadSimple size={16} className="text-[#39FF14]" />
              <div className="text-[10px] text-zinc-400">Drop DK CSV here</div>
            </div>
            <div className="mt-2 mono text-[9px] text-[#39FF14] uppercase text-center">
              → 3 contests · 173 entries loaded
            </div>
          </MockCard>
        </div>
      );
    case "counts":
      return (
        <div className={shared}>
          <MockCard title="Per-Contest Lineup Counts">
            <MockContestRow name="Milly Maker" reserved="150" fill="150" />
            <MockContestRow name="Play Action" reserved="20" fill="20" />
            <MockContestRow name="3-Max" reserved="3" fill="3" />
            <div className="mt-2 mono text-[10px] text-[#39FF14] text-center border-t border-zinc-800 pt-2 uppercase tracking-widest">
              173 unique lineups
            </div>
          </MockCard>
        </div>
      );
    case "optimize":
      return (
        <div className={shared}>
          <MockCard title="Generating Lineups…">
            <MockProgress label="Milly Maker · 150" pct={100} />
            <MockProgress label="Play Action · 20" pct={100} />
            <MockProgress label="3-Max · 3" pct={100} />
            <div className="mt-3 flex items-center justify-center gap-2 text-[#39FF14] mono text-[10px] uppercase tracking-widest">
              <CheckCircle size={12} weight="fill" /> 173 unique lineups ready
            </div>
          </MockCard>
        </div>
      );
    case "download":
      return (
        <div className={shared}>
          <MockCard title="Merged CSV Ready">
            <div className="mono text-[9px] text-zinc-500 leading-relaxed border border-zinc-800 bg-black px-2 py-2 mb-3">
              Entry ID,Contest,QB,RB,RB…<br/>
              3812345678,Milly,Mahomes,…<br/>
              3812345679,Milly,Allen,…<br/>
              <span className="text-zinc-700">... 171 more rows</span>
            </div>
            <PulseButton>DOWNLOAD MERGED CSV</PulseButton>
          </MockCard>
        </div>
      );
    case "push":
      return (
        <div className={shared}>
          <MockCard title="draftkings.com/lineup/upload" host>
            <div className="text-[10px] text-zinc-400 mb-2">Bulk Upload · 173 entries detected</div>
            <div className="mono text-[9px] text-zinc-500 bg-black border border-zinc-800 px-2 py-2 mb-3">
              ✓ Milly Maker · 150 rows<br/>
              ✓ Play Action · 20 rows<br/>
              ✓ 3-Max · 3 rows
            </div>
            <PulseButton dk>IMPORT</PulseButton>
            <div className="mt-3 flex items-center justify-center gap-1 text-[#39FF14] text-[10px] mono uppercase tracking-widest">
              <CheckCircle size={12} weight="fill" /> All lineups locked
            </div>
          </MockCard>
        </div>
      );
    case "ext-download":
      return (
        <div className={shared}>
          <MockCard title="WarPaint Lineups · Extension Card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-[#39FF14] text-black font-black flex items-center justify-center text-sm">W</div>
              <div>
                <div className="text-[10px] text-white font-bold">DK CSV Upload</div>
                <div className="mono text-[8px] text-zinc-500 uppercase tracking-widest">v1.2.0 · 400 KB</div>
              </div>
            </div>
            <PulseButton>↓ DOWNLOAD ZIP</PulseButton>
            <div className="mt-3 mono text-[9px] text-zinc-500 uppercase text-center">
              DKEntries-XXXXXX.csv → upload to WarPaint
            </div>
          </MockCard>
        </div>
      );
    case "ext-install":
      return (
        <div className={shared}>
          <MockCard title="chrome://extensions" host>
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-800">
              <div className="mono text-[9px] text-zinc-400 uppercase tracking-widest">Developer Mode</div>
              <div className="w-8 h-4 bg-[#39FF14] rounded-full relative">
                <div className="absolute right-0.5 top-0.5 w-3 h-3 bg-black rounded-full"></div>
              </div>
            </div>
            <div className="space-y-1.5">
              <InstallStep n={1} text="Unzip the file" done />
              <InstallStep n={2} text="Toggle Developer mode ON" done />
              <InstallStep n={3} text="Click Load unpacked" active />
              <InstallStep n={4} text="Point to unzipped folder" />
            </div>
            <div className="mt-3 border-t border-zinc-800 pt-2 mono text-[9px] text-[#39FF14] uppercase tracking-widest text-center">
              Pin the puzzle-piece icon
            </div>
          </MockCard>
        </div>
      );
    case "ext-use":
      return (
        <div className={shared}>
          <MockCard title="On any DK contest page">
            <div className="border border-zinc-800 bg-black p-3 mb-2 relative">
              <div className="text-[9px] text-zinc-500 uppercase mb-1 tracking-widest mono">DraftKings.com</div>
              <div className="text-[10px] text-white">Milly Maker · Manage</div>
              <div className="mt-6 flex justify-end">
                <div className="inline-flex items-center gap-1.5 bg-[#0a1a10] border-2 border-[#39FF14] px-2 py-1"
                     style={{ boxShadow: "0 0 12px rgba(57,255,20,0.6)", animation: "wp-pulse-ring 1.6s ease-in-out infinite" }}>
                  <div className="w-1.5 h-1.5 bg-[#39FF14] rounded-full"></div>
                  <span className="mono text-[8px] text-[#39FF14] font-bold uppercase tracking-widest">
                    WarPaint · Grab
                  </span>
                </div>
              </div>
            </div>
            <div className="text-[9px] text-zinc-500 text-center leading-relaxed mono">
              → Auto-forwards CSV to WarPaint<br/>
              → Import badge appears in extension popup<br/>
              → Push to DK from popup when ready
            </div>
          </MockCard>
        </div>
      );
    default:
      return null;
  }
}

function MockCard({ title, host, children }) {
  return (
    <div className="border border-zinc-800 bg-black/60"
         style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
      <div className={`px-3 py-1.5 text-[9px] uppercase tracking-widest font-bold border-b border-zinc-800 flex items-center gap-1.5
                      ${host ? "text-[#FF8A00]" : "text-[#39FF14]"}`}>
        {host && <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30]"></span>}
        {!host && <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14]"></span>}
        {title}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function MockRow({ name, pos, locked, boosted, ownArrow }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-zinc-900 last:border-0">
      <div>
        <div className="text-[11px] text-white">{name}</div>
        <div className="text-[9px] text-zinc-500 mono">{pos}</div>
      </div>
      <div className="flex items-center gap-1">
        {locked && <span className="mono text-[8px] px-1.5 py-0.5 bg-[#FF3B30] text-white uppercase">LOCK</span>}
        {boosted && <span className="mono text-[8px] px-1.5 py-0.5 bg-[#39FF14] text-black uppercase">+15%</span>}
        {ownArrow === "down" && <span className="mono text-[8px] px-1.5 py-0.5 bg-[#007AFF] text-white uppercase">-10%</span>}
      </div>
    </div>
  );
}

function MockContest({ name, fee, reserved }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-zinc-900 last:border-0">
      <div className="text-[10px] text-white truncate">{name}</div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="mono text-[9px] text-zinc-400">{fee}</span>
        <span className="mono text-[10px] text-[#39FF14] font-bold">{reserved}</span>
      </div>
    </div>
  );
}

function MockContestRow({ name, reserved, fill }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-zinc-900 last:border-0">
      <div className="text-[10px] text-white">{name}</div>
      <div className="flex items-center gap-2">
        <span className="mono text-[9px] text-zinc-500">{reserved}</span>
        <span className="text-zinc-600">/</span>
        <span className="mono text-[10px] text-[#39FF14] font-bold bg-[#39FF14]/10 border border-[#39FF14]/40 px-1.5">{fill}</span>
      </div>
    </div>
  );
}

function MockProgress({ label, pct }) {
  return (
    <div className="mb-2">
      <div className="flex justify-between text-[9px] text-zinc-400 mb-1">
        <span>{label}</span>
        <span className="mono text-[#39FF14]">{pct}%</span>
      </div>
      <div className="h-1 bg-zinc-900 overflow-hidden">
        <div className="h-full bg-[#39FF14]"
             style={{ width: `${pct}%`, boxShadow: "0 0 8px #39FF14", animation: "wp-slide-in 0.6s ease-out" }} />
      </div>
    </div>
  );
}

function PulseButton({ children, dk }) {
  return (
    <button
      className={`w-full py-2 text-[10px] font-black uppercase tracking-widest transition-colors
                  ${dk ? "bg-[#FF8A00] text-black hover:bg-[#e67c00]"
                       : "bg-[#39FF14] text-black hover:bg-[#2ee30d]"}`}
      style={{ animation: "wp-pulse-ring 1.8s ease-in-out infinite" }}
    >
      {children}
    </button>
  );
}

function InstallStep({ n, text, done, active }) {
  return (
    <div className="flex items-center gap-2 text-[10px]"
         style={active ? { animation: "wp-pulse-ring 1.6s ease-in-out infinite" } : undefined}>
      <span className={`w-4 h-4 flex items-center justify-center mono text-[9px] font-black
                       ${done ? "bg-[#39FF14] text-black"
                              : active ? "bg-[#FF8A00] text-black"
                                       : "bg-zinc-800 text-zinc-500"}`}>
        {done ? "✓" : n}
      </span>
      <span className={done ? "text-zinc-400 line-through decoration-1"
                            : active ? "text-white font-bold"
                                     : "text-zinc-500"}>
        {text}
      </span>
    </div>
  );
}
